import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import classNames from 'classnames';

import { formatPrice } from '@spotahome/soyuz-currency';
import { Mediator } from '@spotahome/soyuz-mediator';

import SpotahomeGuaranteeCircledIcon from '../icons/SpotahomeGuaranteeCircledIcon';

import CheckPropertyEyeCircledIcon from '../icons/CheckPropertyEyeCircledIcon';

import BaseChip from '../BaseChip';

import { getDownPaymentForFirstMonthStrategy } from '../utils/pricing';
import { OPEN_INFORMATION_MODAL } from '../utils/constants';
import {
  APPLY_ON_INCLUDED,
  COST_TYPE_DOWNPAYMENT,
  COST_TYPE_SAH_FEE,
  FREQUENCY_ONE_PAYMENT,
  PAYMENT_METHOD_CREDIT_CARD,
  PAYMENT_METHOD_PAYPAL,
  PAYMENT_TO_SPOTAHOME
} from '../../constants/costs';

import { ListingCostsShape } from '../../constants/shapes';

import PaymentTimelineCard from './PaymentTimelineCard';
import PaymentTimeLineItem from './PaymentTimelineItem';
import PaymentTimelineSubtitle from './PaymentTimelineSubtitle';

import styles from './OnBookingClosed.module.scss';
import cardStyles from './PaymentTimelineCard.module.scss';

import { getUnit, isEstimatedCost } from './costHelpers';

const noop = () => {};

const getDownpaymentCopies = ({ pricing, downPaymentTimeInterval }) => {
  const {
    downPaymentStrategy,
    tenantPaymentsWithCoverage = {},
    checkIn,
    checkOut
  } = pricing;
  const isDownPaymentWeekly = downPaymentTimeInterval.type === 'weekly';
  const isDownPaymentFirstMonthStrategy =
    downPaymentStrategy === 'downPaymentFirstMonthStrategy';

  const downPaymentBaseItem = {
    name: isDownPaymentWeekly
      ? trans(
          `booking_request.spotahome_booking_payment_info.summary.week_advance`,
          {
            weeks: downPaymentTimeInterval.value
          }
        )
      : trans('property_show.book_now_card.first_month'),
    tooltip: isDownPaymentWeekly
      ? trans(
          `booking_request.spotahome_booking_payment_info.week.in.advance.payment`,
          {
            weeks: downPaymentTimeInterval.value
          }
        )
      : trans('property_show.book_now_card.tooltip.first_payment.summary')
  };

  return isDownPaymentFirstMonthStrategy
    ? {
        ...downPaymentBaseItem,
        ...getDownPaymentForFirstMonthStrategy({
          tenantPaymentsWithCoverage,
          checkIn,
          checkOut
        }),
        from: tenantPaymentsWithCoverage?.downPaymentFrom,
        to: tenantPaymentsWithCoverage?.downPaymentTo
      }
    : downPaymentBaseItem;
};

const OnBookingClosed = ({
  isPricingCalculated,
  currencyCode = null,
  pricing = {},
  costs = {},
  downPaymentTimeInterval,
  onSelectDates = noop,
  collapsible = false,
  isCollapsed = true
}) => {
  const selectDatesCopy = trans('book_now_modal.dates_input.label');
  const totalValue = isPricingCalculated
    ? formatPrice(costs.total, currencyCode)
    : selectDatesCopy;

  const totalValueWithoutOffer =
    isPricingCalculated && costs.totalWithoutOffer
      ? formatPrice(costs.totalWithoutOffer, currencyCode)
      : null;

  const handleTotalValueClick = isPricingCalculated ? null : onSelectDates;

  const downpaymentCopies = getDownpaymentCopies({
    pricing,
    downPaymentTimeInterval
  });

  const downpaymentCost = costs?.costs?.find(
    c => c.type === COST_TYPE_DOWNPAYMENT
  );
  const feeCost = costs?.costs?.find(c => c.type === COST_TYPE_SAH_FEE);
  const restCosts = costs?.costs?.filter(
    c => c.type !== COST_TYPE_DOWNPAYMENT && c.type !== COST_TYPE_SAH_FEE
  );

  const downpaymentValue = downpaymentCost?.value ?? selectDatesCopy;
  const feeValue = feeCost?.value ?? selectDatesCopy;

  const subtitle = costs?.total ? (
    <PaymentTimelineSubtitle
      paymentTo={PAYMENT_TO_SPOTAHOME}
      frequency={FREQUENCY_ONE_PAYMENT}
      paymentOptions={[PAYMENT_METHOD_CREDIT_CARD, PAYMENT_METHOD_PAYPAL]}
    />
  ) : null;

  const openInformationModal = (title, body) => () => {
    Mediator.publish(OPEN_INFORMATION_MODAL, { title, body: <p>{body}</p> });
  };

  const alwaysWithSpotahomeTags = [
    {
      label: trans('booking_request.spotahome_booking_payment_info.guarantee'),
      renderLeadingIcon: () => <SpotahomeGuaranteeCircledIcon size="24" />,
      isSpaced: true,
      onClick: openInformationModal(
        trans('amp.property.sah_check.guarantee.title'),
        trans('amp.property.sah_check.guarantee.content')
      )
    },
    {
      label: trans('property_show.book_now_card.24h'),
      renderLeadingIcon: () => <CheckPropertyEyeCircledIcon size="24" />,
      isSpaced: true,
      onClick: openInformationModal(
        trans('amp.property.sah_check.24_hours.title'),
        trans('amp.property.sah_check.24_hours.content')
      )
    }
  ];

  const tagsClasses = classNames(
    styles['on-booking-closed__always-with-spotahome-tags'],
    cardStyles['payment-timeline-card__chips-container']
  );

  let downpaymentSubtitle = null;
  if (downpaymentCopies?.from && downpaymentCopies?.to) {
    downpaymentSubtitle = (
      <PaymentTimelineSubtitle
        from={downpaymentCopies.from}
        to={downpaymentCopies.to}
      />
    );
  }

  return (
    <PaymentTimelineCard
      title={`${trans('listing.book-now-card.first-payment')} + ${trans(
        'booking_request.spotahome_booking_payment_info.summary.our_fee'
      )}`}
      subtitle={subtitle}
      value={totalValue}
      valueWithoutOffer={totalValueWithoutOffer}
      onValueClick={handleTotalValueClick}
      highlighted
      collapsible={collapsible}
      isCollapsed={isCollapsed}
      dataTest="spotahomeBookingPaymentInfo.total"
    >
      <PaymentTimeLineItem
        title={downpaymentCopies.name}
        value={downpaymentValue}
        valueWithoutOffer={downpaymentCost?.valueWithoutOffer || null}
        currencyCode={currencyCode}
        onValueClick={handleTotalValueClick}
        tooltipText={downpaymentCopies.tooltip}
        subtitle={downpaymentSubtitle}
        dataTest="spotahomeBookingPaymentInfo.downPayment"
      />
      <PaymentTimeLineItem
        title={trans(
          'booking_request.spotahome_booking_payment_info.summary.our_fee'
        )}
        value={feeValue}
        valueWithoutOffer={feeCost?.valueWithoutOffer || null}
        currencyCode={currencyCode}
        onValueClick={handleTotalValueClick}
        tooltipText={trans(
          'property_show.book_now_card.one_time_booking_fee.body'
        )}
        dataTestValueWithoutOffer={
          feeCost?.valueWithoutOffer > 0 ? 'promo-code' : null
        }
      />

      {restCosts?.length > 0 &&
        restCosts.map(cost => (
          <PaymentTimeLineItem
            key={cost.type}
            title={
              cost.name ?? trans(`payment.timeline.cost.label.${cost.type}`)
            }
            value={cost.value}
            currencyCode={currencyCode}
            included={cost.applyOn === APPLY_ON_INCLUDED}
            tooltipText={cost.description || null}
            unit={getUnit(cost.applyOn)}
            valueSubtitle={
              isEstimatedCost(cost.applyOn)
                ? trans('payment.timeline.estimated-cost')
                : null
            }
          />
        ))}

      <div className={styles['on-booking-closed__always-with-spotahome']}>
        <div>{trans('value.proposition.features.title.always')}</div>
        <div className={tagsClasses}>
          {alwaysWithSpotahomeTags.map(tag => (
            <BaseChip
              key={tag.label}
              {...tag}
              colorSchema="blue"
              underlined
              size="big"
            />
          ))}
        </div>
      </div>
    </PaymentTimelineCard>
  );
};

OnBookingClosed.propTypes = {
  isPricingCalculated: PropTypes.bool.isRequired,
  currencyCode: PropTypes.string.isRequired,
  pricing: PropTypes.shape({}).isRequired,
  costs: ListingCostsShape,
  downPaymentTimeInterval: PropTypes.shape({
    type: PropTypes.oneOf(['months', 'weeks']),
    value: PropTypes.number
  }).isRequired,
  onSelectDates: PropTypes.func,
  collapsible: PropTypes.bool,
  isCollapsed: PropTypes.bool
};

export default OnBookingClosed;
